import Phaser from "phaser";
import { floatingText } from "@yjsgames/phaser-fx";
import { MODULE_BRICK_BALANCE } from "../data/balance.js";
import {
  createFxRectPool,
  createFxTextPool,
  type FxRectPool,
  type FxTextPool,
} from "../objects/fxImagePool.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { t } from "../i18n/i18n.js";
import { FONT_FAMILY, TYPO } from "./uiTokens.js";

export type BattleProcKind = "normal" | "critical";

export interface BattleProcEvent {
  readonly instanceId: string;
  readonly kind: BattleProcKind;
}

/** 고연발에서 같은 셀 "발동!" FX가 매 발사 중첩되면 WebView draw command가 폭증한다. */
const PROC_FX_MIN_INTERVAL_MS = 180;
/** 한 발사 tick에서 전투탭 그리드 FX가 과도하게 쌓이지 않도록 시각 표시만 상한을 둔다. */
export const PROC_FX_MAX_PER_TICK = 8;
const PROC_TEXT_OFFSET_Y = 7;
const PROC_TEXT_RISE_BY = 10;

export class ModuleGridProcFx {
  private rectPool: FxRectPool | null = null;
  private textPool: FxTextPool | null = null;
  private readonly lastAtByKey = new Map<string, number>();

  constructor(private readonly scene: Phaser.Scene) {}

  beginTick(): number {
    const sceneNow = this.scene.time?.now;
    return Number.isFinite(sceneNow) ? sceneNow : Date.now();
  }

  canEmit(key: string, nowMs: number): boolean {
    const lastAt = this.lastAtByKey.get(key);
    if (lastAt !== undefined && nowMs - lastAt < PROC_FX_MIN_INTERVAL_MS) return false;
    this.lastAtByKey.set(key, nowMs);
    return true;
  }

  /**
   * "발동!" 발동 연출 1회 — 테두리 펄스 + 떠오르는 텍스트. 매 발사 고빈도라 풀(rect/text)에서 재사용한다.
   * 재사용 객체라 위치·크기·fill/stroke·scale·alpha·depth·active/visible을 매번 명시 재설정한다.
   */
  emit(
    centerX: number,
    centerY: number,
    rectW: number,
    rectH: number,
    kind: BattleProcKind = "normal",
  ): void {
    const rectPool = (this.rectPool ??= createFxRectPool(this.scene));
    const textPool = (this.textPool ??= createFxTextPool(this.scene));
    const textColor = kind === "critical" ? MODULE_BRICK_BALANCE.critTextColor : PALETTE.accentAmber;
    const strokeColor =
      kind === "critical" ? parseInt(MODULE_BRICK_BALANCE.critTextColor.replace(/^#/, ""), 16) : PALETTE_NUM.accentAmber;
    const pulse = rectPool
      .acquire()
      .setPosition(centerX, centerY)
      .setSize(rectW, rectH)
      .setFillStyle(PALETTE_NUM.accentAmber, 0)
      .setStrokeStyle(2, strokeColor)
      .setScale(1)
      .setAlpha(1)
      .setDepth(50)
      .setActive(true)
      .setVisible(true);
    this.scene.tweens.add({
      targets: pulse,
      alpha: { from: 0.5, to: 0 },
      scaleX: { from: 1, to: 1.22 },
      scaleY: { from: 1, to: 1.22 },
      duration: 260,
      onComplete: () => rectPool.release(pulse),
    });
    floatingText(this.scene, {
      x: centerX,
      y: centerY - rectH / 2 - PROC_TEXT_OFFSET_Y,
      text: t(kind === "critical" ? "battle.procCritical" : "battle.proc"),
      color: textColor,
      fontFamily: FONT_FAMILY,
      fontSize: TYPO.micro,
      depth: 62,
      riseBy: PROC_TEXT_RISE_BY,
      duration: 280,
      pool: textPool,
    });
  }
}
